import React, { useEffect, useState } from 'react';
import { cachedGeo } from '../JobFieldInputs';
import { input, labelCls } from '../ui/Kit';
import { useCountries } from './OrgSetupTab';

// Part E: where a person works — logistics only (it never feeds scoring). Office / hybrid need an office; remote needs the
// city they work from (city level only — never a street address; visible to company admins and the person only).
// Hybrid may add a "works from home in" city. Same GeoNames pickers as the job form, limited to the city.
export const WORK_MODES = [['office', 'Office'], ['hybrid', 'Hybrid'], ['remote', 'Remote']];

// The validation the server enforces too — returns a message or ''. A company with no offices yet has nothing to choose.
export function workProblem(f, hasOffices = true) {
  if (f.work_mode === 'remote' && !(f.home_country && (f.home_city || '').trim())) return 'Remote: choose the city this person works from';
  if (f.work_mode !== 'remote' && hasOffices && !f.office_location_id) return 'Choose the office this person works from';
  return '';
}

export function WorkLocationFields({ f, set, offices }) {
  const countries = useCountries();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const mode = f.work_mode || 'office';
  const home = mode === 'remote' || (mode === 'hybrid' && f.hybrid_home);
  useEffect(() => {
    if (!f.home_country) { setStates([]); return; }
    cachedGeo(`/states?country=${f.home_country}`).then((r) => setStates(r.states || [])).catch(() => setStates([]));
  }, [f.home_country]);
  useEffect(() => {
    if (!f.home_country || !f.home_state || !(f.home_city || '').trim()) { setCities([]); return undefined; }
    const t = setTimeout(() => {
      cachedGeo(`/cities?country=${f.home_country}&state=${f.home_state}&q=${encodeURIComponent(f.home_city.trim())}`).then((r) => setCities((r.cities || []).slice(0, 8))).catch(() => setCities([]));
    }, 250);
    return () => clearTimeout(t);
  }, [f.home_country, f.home_state, f.home_city]);
  return (
    <fieldset className="space-y-3" data-testid="work-location">
      <legend className={labelCls}>Works from</legend>
      <div className="flex gap-1.5" role="radiogroup" aria-label="Work mode">
        {WORK_MODES.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => set('work_mode', k)} data-testid={`work-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${mode === k ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{l}</button>))}
      </div>
      {mode !== 'remote' && (
        <label className="block"><span className={labelCls}>Office *</span>
          <select className={input} value={f.office_location_id || ''} onChange={(e) => set('office_location_id', e.target.value)} data-testid="work-office-select">
            <option value="">{(offices || []).length ? 'Choose an office…' : 'No offices yet — add one in Org setup'}</option>
            {(offices || []).map((o) => <option key={o.id} value={o.id}>{o.label || o.city}{o.label && o.city ? ` — ${o.city}` : ''}</option>)}</select></label>)}
      {mode === 'hybrid' && (
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.hybrid_home} onChange={(e) => set('hybrid_home', e.target.checked)} data-testid="work-hybrid-home" />
          Also works from home in a different city</label>)}
      {home && (
        <div className="grid grid-cols-3 gap-2" data-testid="work-home">
          <label className="block"><span className={labelCls}>Country {mode === 'remote' ? '*' : ''}</span>
            <select className={input} value={f.home_country || ''} onChange={(e) => { set('home_country', e.target.value); set('home_state', ''); }} data-testid="work-home-country">
              <option value="">Country…</option>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></label>
          <label className="block"><span className={labelCls}>State / region</span>
            <select className={input} value={f.home_state || ''} disabled={!states.length} onChange={(e) => set('home_state', e.target.value)} data-testid="work-home-state">
              <option value="">{states.length ? 'State…' : '—'}</option>{states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></label>
          <label className="block"><span className={labelCls}>City {mode === 'remote' ? '*' : ''}</span>
            <input className={input} list="work-city-options" value={f.home_city || ''} maxLength={100} onChange={(e) => set('home_city', e.target.value)} data-testid="work-home-city" />
            <datalist id="work-city-options">{cities.map((c) => <option key={c} value={c} />)}</datalist></label>
          <p className="col-span-3 text-xs text-[#64748B]">City level only — no street address. Only company admins and the person see it.</p>
        </div>)}
    </fieldset>
  );
}
